import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Container, Card, Tabs, Tab, Button, Offcanvas, Badge, Table, Dropdown } from 'react-bootstrap';
import { BiPencil } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import {
    FaRegClock,
    FaUserEdit,
    FaFileInvoice,
    FaTag,
    FaAddressCard,
    FaBuilding,
    FaUser,
    FaStickyNote,
    FaArrowRight,
    FaFileAlt,
    FaReceipt,
    FaShieldAlt
} from "react-icons/fa";
import moment from "moment";
import DealView from './DealView';
import SelectedContact from '../../contact/SelectedContact';
import withPermission from '../../../utils/withPermission';
import ActivityList from '../lead/activity/ActivityList';
import DealModal from './DealModal';
import DealPermissions from './DealPermissions';
import QuotationPreviewModal from '../../quotation/QuotationPreviewModal';
import { useAuth } from '../../../context/AuthContext';
import QuotationForm from '../../quotation/QuotationForm';
import QuotationUploadOffcanvas from './QuotationUploadOffcanvas';
import { BsPlus, BsPencil, BsTrash, BsSearch, BsThreeDotsVertical } from "react-icons/bs";
import OrganisationDetails from "../organisation/OrganisationDetails";



const WithContactPermission = withPermission(({ children }) => children, ['contact-list']);
const WithActivityPermission = withPermission(({ children }) => children, ['activity-list']);

const DealDetails = ({ show, onHide, dealId }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get("activeTab") || "activityList";
    const [dealDetails, setDealDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('activityList');
    const { roles, dataScopes } = useAuth();
    const isAllowedToReassign = roles?.some(role => role.toLowerCase() === 'superadmin') ||
        dataScopes?.some(scope => scope === 'all' || scope === 'team');
    const [activities, setActivities] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [poDetails, setPODetails] = useState(null);
    const [showQuotationPreviewModal, setShowQuotationPreviewModal] = useState(false);
    const [selectedQuotationForPreview, setSelectedQuotationForPreview] = useState(null);
    const [showQuotationModal, setShowQuotationModal] = useState(false);
    const [selectedQuotationId, setSelectedQuotationId] = useState(null);
    const [selectedQuotationData, setSelectedQuotationData] = useState(null);
    const [showUploadedQuotationEdit, setShowUploadedQuotationEdit] = useState(false);
    const [selectedUploadedQuotation, setSelectedUploadedQuotation] = useState(null);
    const [isOrder, setIsOrder] = useState(false);
    const [products, setProducts] = useState([]);
    const [productsFetched, setProductsFetched] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDeal, setSelectedDeal] = useState(null);
    const [timelineUpdates, setTimelineUpdates] = useState([]);
    const [Deals, setDeal] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [organisations, setOrganisation] = useState([]);
    const [reopenOffcanvas, setReopenOffcanvas] = useState(false);

    const didFetchRef = useRef(false);
    const [localShow, setLocalShow] = useState(show);
    const [isMinimizing, setIsMinimizing] = useState(false);

    const [showOrganisationDetails, setShowOrganisationDetails] = useState(false);
    const [selectedOrganisationIdForDetails, setSelectedOrganisationIdForDetails] = useState(null);

    const handleShowOrganisationDetails = (organisationId) => {
        setSelectedOrganisationIdForDetails(organisationId);
        setShowOrganisationDetails(true);
    };

    const handleCloseOrganisationDetails = () => {
        setShowOrganisationDetails(false);
        setSelectedOrganisationIdForDetails(null);
    };

    // Sync with parent show prop
    useEffect(() => {
        if (!isMinimizing) {
            setLocalShow(show);
        }
    }, [show, isMinimizing]);

    // Fetch quotations using the deal's quotation_id
    const fetchQuotations = async () => {
        try {
            if (!dealDetails?.quotation_id) {
                setQuotations([]);
                return;
            }

            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Fetch the main quotation
            const mainQuotationResponse = await axios.get(`${backendUrl}/api/quotations/${dealDetails.quotation_id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const mainQuotation = mainQuotationResponse.data.quotation;

            // If we need to fetch related quotations (versions), we can do it here
            const quotationsList = mainQuotationResponse.data.versions || [mainQuotationResponse.data.quotation];

            const formattedQuotations = quotationsList.map((quotation, index) => {
                const version = quotation.version || (index + 1);
                return {
                    ...quotation,
                    version: version,
                    displayRef: quotation.ref_no
                };
            });
            setQuotations(formattedQuotations);
        } catch (error) {
            console.error("Error fetching quotations:", error);
            setQuotations([]);
        }
    };

    // Group quotations by original and create versions
    const groupQuotationsByVersion = (quotationsList) => {
        const grouped = {};

        quotationsList.forEach(quotation => {
            const originalId = quotation.original_quotation_id || quotation.id;

            if (!grouped[originalId]) {
                grouped[originalId] = [];
            }

            // Add version number
            const version = grouped[originalId].length + 1;
            grouped[originalId].push({
                ...quotation,
                version: version,
                displayRef: version === 1 ? quotation.ref_no : `${quotation.ref_no}-${version}`
            });
        });

        // Flatten the grouped object into an array
        return Object.values(grouped).flat();
    };

    // Fetch PO details from deal data
    const fetchPODetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = dealId || id;
            const response = await axios.get(`${backendUrl}/api/deals/${currentId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const dealData = response.data.data;
            setPODetails({
                po_attachment: dealData.po_attachment,
                po_value: dealData.po_value,
                issue_date: dealData.issue_date,
                po_number: dealData.po_number
            });
        } catch (error) {
            console.error("Error fetching PO details:", error);
        }
    };

    const fetchTimelineUpdates = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            let params = {};
            params.deal_id = dealId || id;

            if (!params.deal_id) {
                console.warn("Missing Deal_id, skipping API call.");
                return;
            }

            const response = await axios.get(`${backendUrl}/api/history/all`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
                params,
            });

            setTimelineUpdates(response.data.data);
        } catch (error) {
            console.error("Error fetching timeline updates:", error);
        }
    };

    const fetchDealDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = dealId || id;
            const response = await axios.get(`${backendUrl}/api/deals/${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });

            const dealData = response.data.data;
            setDealDetails(dealData);

            // If deal has quotation_id, fetch quotations
            if (dealData.quotation_id) {
                await fetchQuotationsForDeal(dealData.quotation_id);
            } else {
                setQuotations([]);
            }

        } catch (error) {
            console.error('Error fetching Deal details:', error);
        }
    };

    // Enhanced function to fetch quotations using the deal's quotation_id
    const fetchQuotationsForDeal = async (quotationId) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Fetch the main quotation
            const mainQuotationResponse = await axios.get(`${backendUrl}/api/quotations/${quotationId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const mainQuotation = mainQuotationResponse.data.quotation;

            // If you have a way to fetch quotation versions/history, add it here
            const quotationsList = mainQuotationResponse.data.versions || [mainQuotationResponse.data.quotation];

            const formattedQuotations = quotationsList.map((quotation, index) => {
                const version = quotation.version || (index + 1);
                return {
                    ...quotation,
                    version: version,
                    displayRef: quotation.ref_no
                };
            });
            setQuotations(formattedQuotations);
        } catch (error) {
            console.error("Error fetching quotations:", error);
            setQuotations([]);
        }
    };

    // Fetch activities
    const fetchActivities = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/activities`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    deal_id: dealId || id,
                },
                skipLoader: true,
            });

            setActivities(response.data.data || []);
        } catch (error) {
            console.error('Error fetching activities:', error);
        }
    };

    const fetchContacts = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contact`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const filteredContacts = response.data.Data.filter(
                contact => contact.type !== "instance" && contact.type !== "tenant"
            );

            setContacts(filteredContacts);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const fetchOrganisation = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/organisation`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setOrganisation(response.data?.data|| response.data?.data?.data || []);
        } catch (error) {
            console.error(`Error fetching organisations:`, error);
        }
    };

    const editDeal = async (contactId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.put(`${backendUrl}/api/deals/${contactId}`, updatedData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            if (response.data.success || response.status === 200) {
                await fetchDealDetails();
                await fetchTimelineUpdates();
                await fetchActivities();
            }

            setShowModal(false);
            if (reopenOffcanvas) {
                setReopenOffcanvas(false);
                setTimeout(() => {
                    onHide(false);
                }, 100);
            }
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            } else {
                console.error(`Error updating Deal:`, error);
            }
        }
    };

    // Handle quotation edit
    const handleEditQuotation = async (quotationId) => {
        try {
            setSelectedQuotationId(quotationId);

            // Fetch the quotation data for editing
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/quotations/${quotationId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const q = response.data.quotation;
            if (q?.is_uploaded) {
                let targetQuot = q;
                if (response.data.versions && response.data.versions.length > 0) {
                    const sorted = [...response.data.versions].sort((a, b) => (b.version ?? 0) - (a.version ?? 0) || b.id - a.id);
                    if (sorted[0]) {
                        targetQuot = sorted[0];
                    }
                }
                setSelectedUploadedQuotation(targetQuot);
                setIsMinimizing(true);
                setLocalShow(false); // Close DealDetails so it does not stay open behind edit
                setShowUploadedQuotationEdit(true);
                setShowQuotationPreviewModal(false);
                return;
            }

            setSelectedQuotationData(response.data.quotation);
            setIsMinimizing(true);
            setShowQuotationModal(true);
            // setLocalShow(false);
            setShowQuotationPreviewModal(false); // Close preview when opening edit

        } catch (error) {
            console.error("Error fetching quotation data:", error);
        }
    };

    // Handle edit from preview modal
    const handleEditFromPreview = () => {
        if (selectedQuotationForPreview) {
            setShowQuotationPreviewModal(false);
            handleEditQuotation(selectedQuotationForPreview.id);
        }
    };

    // Edit quotation function
    const editQuotation = async (quotationId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/quotations/${quotationId}?_method=PUT`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                    'Tenant-Origin': origin,
                },
            });

            const updatedQuotation = response.data.quotation;

            if (updatedQuotation) {
                // Refresh quotations list
                if (dealDetails?.quotation_id) {
                    await fetchQuotationsForDeal(dealDetails.quotation_id);
                }

                setShowQuotationModal(false);
                setSelectedQuotationId(null);
                setSelectedQuotationData(null);

                // Show the updated quotation in preview
                setSelectedQuotationForPreview(updatedQuotation);
                setIsMinimizing(true);
                setShowQuotationPreviewModal(true);
                // setLocalShow(false);
            }
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            } else {
                console.error('Error updating quotation:', error);
            }
        }
    };

    // Add quotation function
    const addQuotation = async (newQuotation) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/quotations`, newQuotation, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                    'Tenant-Origin': origin,
                },
            });

            const createdQuotation = response.data.quotation;

            if (createdQuotation) {
                // Update deal with quotation_id
                await axios.put(`${backendUrl}/api/deals/${dealId || id}`, {
                    quotation_id: createdQuotation.id
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });

                // Refresh deal details and quotations
                await fetchDealDetails();

                setShowQuotationModal(false);
                setSelectedQuotationForPreview(createdQuotation);
                setShowQuotationPreviewModal(true);
                // setLocalShow(false);
            }
        } catch (error) {
            console.error("Error adding quotation:", error);
        }
    };

    useEffect(() => {
        if (localShow && (dealId || id)) {
            setDealDetails(null);
            fetchDealDetails();
            fetchActivities();
        }
    }, [localShow, dealId]);

    useEffect(() => {
        if (localShow && activeTab === 'timeline') {
            fetchTimelineUpdates();
        } else if (localShow && activeTab === 'poDetails') {
            fetchPODetails();
        }
        // quotations are now fetched automatically when dealDetails are loaded
    }, [localShow, activeTab]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleOffcanvasClose = () => {
        if (isMinimizing) return;
        setDealDetails(null);
        setActiveTab('activityList');
        didFetchRef.current = false;
        onHide();
    };

    // Handle quotation view
    const handleViewQuotation = async (quotationId) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/quotations/${quotationId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            // View the exact quotation version that was clicked
            const quotationToShow = response.data.quotation;

            setSelectedQuotationForPreview(quotationToShow);
            setIsMinimizing(true);
            setShowQuotationPreviewModal(true);
            // setLocalShow(false);
        } catch (error) {
            console.error("Error fetching quotation for preview:", error);
        }
    };

    // Handle download PDF
    const handleDownloadPDF = async (quotationId, refNo) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/quotations/${quotationId}/download`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Quotation_${refNo}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading PDF:', error);
        }
    };

    // Handle create new quotation
    const handleCreateQuotation = () => {
        setSelectedQuotationId(null);
        setSelectedQuotationData(null);
        setIsMinimizing(true);
        setShowQuotationModal(true);
        // setLocalShow(false);
    };

    // Check if deal has quotation
    const hasQuotation = dealDetails?.quotation_id;

    const getIcon = (action) => {
        switch (action) {
            case "Opportunity Created":
                return <div className="timeline-icon-circle bg-success"><FaRegClock className="text-white" size={12} /></div>;
            case "Opportunity Updated":
                return <div className="timeline-icon-circle bg-primary"><FaUserEdit className="text-white" size={12} /></div>;
            case "Contact Added":
                return <div className="timeline-icon-circle bg-info"><FaAddressCard className="text-white" size={12} /></div>;
            case "status":
                return <div className="timeline-icon-circle bg-warning"><FaTag className="text-white" size={12} /></div>;
            default:
                return <div className="timeline-icon-circle bg-secondary"><FaRegClock className="text-white" size={12} /></div>;
        }
    };

    const groupByDate = (updates) => {
        return updates.reduce((grouped, update) => {
            const date = moment(update.created_at || new Date()).format("MMM DD, YYYY");
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(update);
            return grouped;
        }, {});
    };

    const groupedUpdates = groupByDate(timelineUpdates);

    // Enhanced Attachment Preview Component for PO
    // const AttachmentPreview = ({ attachmentUrl, showOriginalName = false }) => {
    //     if (!attachmentUrl) return null;

    //     const url = `${process.env.REACT_APP_BACKEND_URL}/storage/${attachmentUrl}`;
    //     const fileName = attachmentUrl.split('/').pop();
    //     const fileType = fileName.split('.').pop().toLowerCase();

    //     const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileType);
    //     const isPDF = fileType === 'pdf';
    //     const isDocument = ['doc', 'docx', 'txt'].includes(fileType);
    //     const isSpreadsheet = ['xls', 'xlsx', 'csv'].includes(fileType);

    //     const getFileIcon = () => {
    //         if (isPDF) return 'mdi-file-pdf-box text-danger';
    //         if (isImage) return 'mdi-file-image text-success';
    //         if (isDocument) return 'mdi-file-document text-primary';
    //         if (isSpreadsheet) return 'mdi-file-excel text-success';
    //         return 'mdi-file-document-outline text-secondary';
    //     };

    //     return (
    //         <div className="border rounded bg-white p-3">
    //             <div className="d-flex align-items-center justify-content-between mb-3">
    //                 <div className="d-flex align-items-center">
    //                     <i className={`mdi ${getFileIcon()} display-6 me-3`}></i>
    //                     <div>
    //                         <h6 className="mb-1 text-dark">{showOriginalName ? fileName : 'PO Attachment'}</h6>
    //                         <small className="text-muted">
    //                             {fileType.toUpperCase()} • {moment().format('MMM DD, YYYY')}
    //                         </small>
    //                     </div>
    //                 </div>
    //                 <Badge bg={isPDF ? "danger" : isImage ? "success" : "primary"}>
    //                     {fileType.toUpperCase()}
    //                 </Badge>
    //             </div>

    //             <div className="border rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
    //                 {isImage ? (
    //                     <img
    //                         src={url}
    //                         alt="PO Attachment"
    //                         className="img-fluid w-100"
    //                         style={{ maxHeight: '350px', objectFit: 'contain' }}
    //                     />
    //                 ) : isPDF ? (
    //                     <iframe
    //                         src={`${url}#toolbar=0&navpanes=0&view=FitH`}
    //                         title="PO Attachment"
    //                         width="100%"
    //                         height="350px"
    //                         frameBorder="0"
    //                         className="border-0"
    //                     />
    //                 ) : (
    //                     <div className="text-center py-5">
    //                         <i className={`mdi ${getFileIcon()} display-1 text-muted mb-3`}></i>
    //                         <p className="text-muted mb-2">Preview not available</p>
    //                         <small className="text-muted">
    //                             Download the file to view {isDocument ? 'document' : isSpreadsheet ? 'spreadsheet' : 'content'}
    //                         </small>
    //                     </div>
    //                 )}
    //             </div>
    //         </div>
    //     );
    // };

    const AttachmentPreview = ({ attachmentUrl, showOriginalName = false }) => {
        if (!attachmentUrl) return null;

        const url = `${process.env.REACT_APP_BACKEND_URL}/storage/${attachmentUrl}`;
        const fileName = attachmentUrl.split('/').pop();
        const fileType = fileName.split('.').pop().toLowerCase();

        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileType);
        const isPDF = fileType === 'pdf';
        const isDocument = ['doc', 'docx', 'txt'].includes(fileType);
        const isSpreadsheet = ['xls', 'xlsx', 'csv'].includes(fileType);

        const getFileIcon = () => {
            if (isPDF) return 'mdi-file-pdf-box text-danger';
            if (isImage) return 'mdi-file-image text-success';
            if (isDocument) return 'mdi-file-document text-primary';
            if (isSpreadsheet) return 'mdi-file-excel text-success';
            return 'mdi-file-document-outline text-secondary';
        };

        return (
            <div className="border rounded bg-white p-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                        <i className={`mdi ${getFileIcon()} display-6 me-3`}></i>
                        <div>
                            <h6 className="mb-1 text-dark">{showOriginalName ? fileName : 'PO Attachment'}</h6>
                            <small className="text-muted">
                                {fileType.toUpperCase()} • {moment().format('MMM DD, YYYY')}
                            </small>
                        </div>
                    </div>
                    <Badge bg={isPDF ? "danger" : isImage ? "success" : "primary"}>
                        {fileType.toUpperCase()}
                    </Badge>
                </div>

                {/* Fixed container - no scrollbars, fixed size */}
                <div className="border rounded bg-light d-flex justify-content-center align-items-center p-2"
                    style={{
                        width: '100%',
                        height: '600px',
                        overflow: 'hidden'
                    }}>
                    {isImage ? (
                        <img
                            src={url}
                            alt="PO Attachment"
                            style={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    ) : isPDF ? (
                        <iframe
                            src={`${url}#toolbar=0&navpanes=0&view=FitH`}
                            title="PO Attachment"
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            style={{
                                border: 'none',
                                overflow: 'hidden'
                            }}
                        />
                    ) : (
                        <div className="text-center py-5">
                            <i className={`mdi ${getFileIcon()} display-1 text-muted mb-3`}></i>
                            <p className="text-muted mb-2">Preview not available</p>
                            <small className="text-muted">
                                Download the file to view {isDocument ? 'document' : isSpreadsheet ? 'spreadsheet' : 'content'}
                            </small>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    return (
        <>
            <Offcanvas
                show={localShow}
                onHide={handleOffcanvasClose}
                placement="end"
                style={{
                    width: '1050px',
                    ...(isMinimizing ? { visibility: 'hidden', opacity: 0, pointerEvents: 'none' } : {})
                }}
                backdropClassName={isMinimizing ? 'd-none' : ''}
                className={`lead-offcanvas ${isMinimizing ? 'minimized' : ''}`}
                enforceFocus={false}
                autoFocus={false}
                restoreFocus={false}
                unmountOnExit={false}
            >

                {/* Offcanvas Header */}
                <Offcanvas.Header className="border-bottom bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle p-2 me-3">
                                <FaUser className="text-white" size={18} />
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold text-dark">{dealDetails?.name}</h5>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                    setSelectedDeal(dealDetails);
                                    setIsMinimizing(true);
                                    setShowModal(true);
                                    // setLocalShow(false);
                                }}
                            >
                                <BiPencil className="me-1" />
                                Edit Deal
                            </Button>
                            <Button
                                variant="light"
                                size="sm"
                                onClick={handleOffcanvasClose}
                                className="border-0"
                            >
                                <BsX size={20} />
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Header>

                {/* Offcanvas Body */}
                <Offcanvas.Body className="p-0">
                    {/* Deal Summary Card */}
                    <div className="p-4 border-bottom bg-light">
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                        <FaBuilding className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Organisation</label>
                                        <h6 
                                            className="mb-0 fw-semibold text-dark" 
                                            onClick={() => dealDetails?.organisation?.id && handleShowOrganisationDetails(dealDetails.organisation.id)}
                                            style={{ cursor: 'pointer', color: '#2F7EF7' }}
                                        >
                                            {dealDetails?.organisation?.name || 'Not assigned'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                        <FaTag className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Lead Source</label>
                                        <h6 className="mb-0 fw-semibold text-dark text-capitalize">
                                            {dealDetails?.lead_source ? dealDetails.lead_source.replace('_', ' ') : 'Not specified'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <div className="bg-white rounded-circle p-2 shadow-sm me-3 mt-1">
                                        <FaStickyNote className="text-primary" size={16} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <label className="text-muted small mb-1 d-block">Customer Type</label>
                                        <div>
                                            <h6 className="mb-0 fw-semibold text-dark text-capitalize">
                                                {dealDetails?.customer_type ? dealDetails.customer_type.replace('_', ' ') : 'Not specified'}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {dealDetails?.lead_source === 'partner' && (
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                            <i className="mdi mdi-swap-horizontal text-primary" style={{ fontSize: '16px' }}></i>
                                        </div>
                                        <div>
                                            <label className="text-muted small mb-1 d-block">Partner Name</label>
                                            <h6 className="mb-0 fw-semibold text-dark text-capitalize">
                                                {dealDetails?.partner_organisation?.name || dealDetails?.partner_name || dealDetails?.partner?.name || 'Not specified'}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {dealDetails?.lead_source === 'partner' && dealDetails?.partner_organisation && (
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                            <i className="mdi mdi-handshake text-primary" style={{ fontSize: '16px' }}></i>
                                        </div>
                                        <div>
                                            <label className="text-muted small mb-1 d-block">Partner</label>
                                            <h6 className="mb-0 fw-semibold text-dark">
                                                {dealDetails?.partner_organisation?.name || dealDetails?.partner_name || dealDetails?.partner?.name || 'Not specified'}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                        <FaRegClock className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Created Date</label>
                                        <h6 className="mb-0 fw-semibold text-dark">
                                            {dealDetails?.created_date ? moment(dealDetails.created_date).format('DD-MM-YYYY') : dealDetails?.created_at ? moment(dealDetails.created_at).format('DD-MM-YYYY') : '-'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="border-bottom bg-white">
                        <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                            <WithActivityPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'activityList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('activityList')}
                                        role="tab"
                                    >
                                        <FaRegClock className="me-2" size={14} />
                                        Activities

                                    </button>
                                </li>
                            </WithActivityPermission>
                            <WithContactPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'contactList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('contactList')}
                                        role="tab"
                                    >
                                        <FaUser className="me-2" size={14} />
                                        Contacts
                                    </button>
                                </li>
                            </WithContactPermission>

                            {/* Quotations Tab */}
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotations' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotations')}
                                    role="tab"
                                >
                                    <FaFileAlt className="me-2" size={14} />
                                    Quotations

                                </button>
                            </li>

                            {/* PO Details Tab */}
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'poDetails' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('poDetails')}
                                    role="tab"
                                >
                                    <FaReceipt className="me-2" size={14} />
                                    PO Details
                                </button>
                            </li>

                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'dealDetails' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('dealDetails')}
                                    role="tab"
                                >
                                    <FaBuilding className="me-2" size={14} />
                                    Details
                                </button>
                            </li>

                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'timeline' ? 'active' : ''}`}
                                    onClick={() => {
                                        handleTabClick('timeline');
                                        fetchTimelineUpdates();
                                    }}
                                    role="tab"
                                >
                                    <FaRegClock className="me-2" size={14} />
                                    Timeline

                                </button>
                            </li>

                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'permissions' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('permissions')}
                                    role="tab"
                                >
                                    <FaShieldAlt className="me-2" size={14} />
                                    Assign
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Tab Content */}
                    <div className="tab-content p-4 bg-white" style={{ minHeight: '500px', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>

                        {/* Contact List Tab */}
                        {activeTab === "contactList" && dealDetails && (
                            <div className="tab-pane active">
                                <SelectedContact
                                    dealId={dealDetails?.id}
                                    entityType="deal"
                                    fetchDealData={fetchDealDetails}
                                    dealData={dealDetails}
                                    setDealDetails={setDealDetails}
                                    onCloseOffcanvas={() => {
                                        setIsMinimizing(true);
                                        // setLocalShow(false);
                                    }}
                                    onReopenOffcanvas={() => {
                                        setIsMinimizing(false);
                                        // setLocalShow(true);
                                    }}
                                />
                            </div>
                        )}

                        {/* Activity List Tab */}
                        {activeTab === "activityList" && dealDetails && (
                            <div className="tab-pane active">
                                <ActivityList
                                    dealId={dealDetails?.id}
                                    type='deal'
                                    fetchDealData={fetchDealDetails}
                                    leadData={dealDetails}
                                    setDealDetails={setDealDetails}
                                    onModalShow={() => {
                                        setIsMinimizing(true);
                                        // setLocalShow(false);
                                    }}
                                    onModalClose={() => {
                                        setIsMinimizing(false);
                                        // setLocalShow(true);
                                    }}
                                />
                            </div>
                        )}

                        {/* Deal Details Tab */}
                        {activeTab === "dealDetails" && (
                            <div className="tab-pane active">
                                {dealDetails ? (
                                    <DealView
                                        data={dealDetails}
                                        dealId={dealId || id}
                                        setDealDetails={setDealDetails}
                                    />
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Loading Deal details...</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Quotations Tab */}
                        {activeTab === "quotations" && (
                            <div className="tab-pane active">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0">Quotations</h5>
                                    <div className="d-flex align-items-center gap-2">

                                        {/* <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleCreateQuotation}
                                        className="d-flex align-items-center"
                                    >
                                        <i className="mdi mdi-plus me-1"></i>
                                        Create Quotation
                                    </Button> */}
                                    </div>
                                </div>

                                {hasQuotation ? (
                                    quotations.length > 0 ? (
                                        <div className="contacts-table-modern">
                                            <div className="table-responsive">
                                                <table className="table table-modern">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Ref No</th>
                                                            <th>Amount</th>

                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {quotations.map((quotation) => (
                                                            <tr key={quotation.id}>
                                                                <td>
                                                                    {quotation.created_at 
                                                                        ? moment(quotation.created_at).format('MMM DD, YYYY') 
                                                                        : quotation.date 
                                                                            ? moment(quotation.date).format('MMM DD, YYYY') 
                                                                            : '--'
                                                                    }
                                                                </td>
                                                                <td>
                                                                    <button className='dropdown-item'
                                                                        onClick={() => handleViewQuotation(quotation.id)}
                                                                    >

                                                                        <strong>{quotation.displayRef}</strong>
                                                                    </button>
                                                                </td>

                                                                <td>
                                                                    {Number(quotation.amount || 0).toLocaleString('en-US', {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2
                                                                    })}
                                                                </td>



                                                                <td className="text-center">
                                                                    <Dropdown drop='up'>
                                                                        <Dropdown.Toggle
                                                                            variant="outline-light"
                                                                            size="sm"
                                                                            className="border-0 shadow-none"
                                                                            id={`dropdown-${quotation.id}`}
                                                                        >
                                                                            <BsThreeDotsVertical className="text-muted" />
                                                                        </Dropdown.Toggle>
                                                                        <Dropdown.Menu className="font-size-14">
                                                                            <Dropdown.Item
                                                                                onClick={() => handleViewQuotation(quotation.id)}
                                                                                className="d-flex align-items-center gap-2"
                                                                            >

                                                                                View
                                                                            </Dropdown.Item>


                                                                            <Dropdown.Item
                                                                                onClick={() => handleEditQuotation(quotation.id)}
                                                                                className="d-flex align-items-center gap-2"
                                                                            >

                                                                                Edit
                                                                            </Dropdown.Item>


                                                                            <Dropdown.Item
                                                                                onClick={() => handleDownloadPDF(quotation.id, quotation.displayRef)}
                                                                                className="d-flex align-items-center gap-2 text-danger"
                                                                            >

                                                                                Download
                                                                            </Dropdown.Item>


                                                                        </Dropdown.Menu>
                                                                    </Dropdown>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">Loading quotations...</p>
                                        </div>
                                    )
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="bg-light rounded p-5">
                                            <FaFileAlt className="text-muted mb-3" size={32} />
                                            <h6 className="text-muted">No Quotations Found</h6>
                                            <p className="text-muted small mb-3">
                                                No quotations have been created for this deal yet.
                                            </p>
                                            <Button
                                                variant="primary"
                                                onClick={handleCreateQuotation}
                                            >
                                                Create First Quotation
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* PO Details Tab */}
                        {activeTab === "poDetails" && (
                            <div className="tab-pane active">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h5 className="mb-0 text-dark">Purchase Order Details</h5>
                                    <Badge bg={poDetails?.po_attachment ? "success" : "secondary"} className="fs-6">
                                        {poDetails?.po_attachment ? "PO Attached" : "No PO"}
                                    </Badge>
                                </div>

                                {poDetails ? (
                                    <div className="row">
                                        {/* PO Attachment Card */}
                                        <div className="col-12">
                                            <Card className="border-0 shadow-sm">
                                                <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                    <h6 className="mb-0 text-dark">
                                                        <i className="mdi mdi-paperclip me-2 text-primary"></i>
                                                        PO Attachment
                                                    </h6>
                                                    <div className="d-flex flex-wrap gap-4 align-items-center bg-light rounded px-3 py-2 border">
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-muted small me-2">PO No:</span>
                                                            <span className="fw-semibold text-dark">{poDetails?.po_number || 'N/A'}</span>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-muted small me-2">Date:</span>
                                                            <span className="fw-semibold text-dark">
                                                                {poDetails?.issue_date ? moment(poDetails.issue_date).format('MMM DD, YYYY') : 'N/A'}
                                                            </span>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-muted small me-2">Value:</span>
                                                            <span className="fw-bold text-success">
                                                                {poDetails?.po_value ? Number(poDetails.po_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body className="p-4">
                                                    {poDetails?.po_attachment ? (
                                                        <div className="">
                                                            <div style={{ width: '100%', height: '80vh' }}>
                                                                <AttachmentPreview
                                                                    attachmentUrl={poDetails.po_attachment}
                                                                    showOriginalName={true}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-5">
                                                            <div className="bg-light rounded p-5">
                                                                <i className="mdi mdi-file-remove-outline display-4 text-muted mb-3"></i>
                                                                <h6 className="text-muted">No PO Attachment</h6>
                                                                <p className="text-muted small mb-3">
                                                                    No purchase order attachment has been uploaded for this deal.
                                                                </p>
                                                                <Badge bg="secondary" className="fs-7">
                                                                    Awaiting Upload
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </div>

                                        {/* PO Attachment Card */}
                                        {/* <div className="col-12">
                                        <Card className="border-0 shadow-sm">
                                            <Card.Header className="bg-white border-bottom-0 py-3">
                                                <h6 className="mb-0 text-dark">
                                                    <i className="mdi mdi-paperclip me-2 text-primary"></i>
                                                    PO Attachment
                                                </h6>
                                            </Card.Header>
                                            <Card.Body className="p-4">
                                                {poDetails.po_attachment ? (
                                                    <div className="row align-items-center">
                                                        <div className="col-md-8">
                                                            <AttachmentPreview
                                                                attachmentUrl={poDetails.po_attachment}
                                                                showOriginalName={true}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <div className="bg-light rounded p-5">
                                                            <i className="mdi mdi-file-remove-outline display-4 text-muted mb-3"></i>
                                                            <h6 className="text-muted">No PO Attachment</h6>
                                                            <p className="text-muted small mb-3">
                                                                No purchase order attachment has been uploaded for this deal.
                                                            </p>
                                                            <Badge bg="secondary" className="fs-7">
                                                                Awaiting Upload
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </div> */}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Loading PO details...</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Timeline Tab */}
                        {activeTab === "timeline" && dealDetails && (
                            <div className="tab-pane active">
                                {timelineUpdates.length > 0 ? (
                                    <div className="timeline-vertical">
                                        <div className="timeline">
                                            {timelineUpdates.length > 0 && Object.entries(groupByDate(timelineUpdates))
                                                .sort(([dateA], [dateB]) => new Date(dateB === "Invalid date" ? new Date() : dateB) - new Date(dateA === "Invalid date" ? new Date() : dateA))
                                                .map(([date, updates]) => (
                                                    <div key={date} className="timeline-date-group">
                                                        <div className="timeline-date-box font-size-13">{date}</div>
                                                        {updates
                                                            .sort((a, b) => new Date(b.created_at || new Date()) - new Date(a.created_at || new Date()))
                                                            .map((update, index) => {
                                                                let title = update.action;
                                                                if (title && title.includes('Deal')) {
                                                                    title = title.replace(/Deal/gi, 'Opportunity');
                                                                }
                                                                let details = null;

                                                                const formatFieldName = (field) => {
                                                                    return field.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
                                                                };

                                                                if (update.history_type === "activity") {
                                                                    if (update.action && update.action.endsWith("added")) {
                                                                        title = update.data?.subject || "Activity";
                                                                    } else if (update.action && update.action.endsWith("updated")) {
                                                                        title = `${update.data?.subject || 'Activity'} updated`;
                                                                        if (update.data?.changes) {
                                                                            details = Object.entries(update.data.changes)
                                                                                .filter(([key]) => !["created_at", "updated_at", "deal", "client_id", "project_id", "quotation_id"].includes(key))
                                                                                .map(([key, change]) => (
                                                                                    <div key={key}>
                                                                                        <strong>{formatFieldName(key)}</strong> changed from <strong>{typeof change.old === 'object' && change.old !== null ? (change.old.name || change.old.id || '[Object]') : String(change.old || '-')}</strong> to <strong>{typeof change.new === 'object' && change.new !== null ? (change.new.name || change.new.id || '[Object]') : String(change.new || '-')}</strong>
                                                                                    </div>
                                                                                ));
                                                                        }
                                                                    }
                                                                } else if (update.history_type === "deal") {
                                                                    if (update.action === "Deal Reassigned" || update.action === "Opportunity Reassigned") {
                                                                        title = `Opportunity Reassigned`;
                                                                        details = (
                                                                            <div>
                                                                                From <strong>{update.data?.from || 'N/A'}</strong> to <strong>{update.data?.to || 'N/A'}</strong>
                                                                            </div>
                                                                        );
                                                                    } else if (update.data?.old?.stage !== undefined && update.data?.new?.stage !== undefined && update.data.old.stage !== update.data.new.stage) {
                                                                        title = `Stage changed from ${update.data.old.stage} to ${update.data.new.stage}`;
                                                                        details = `Probability changed from ${update.data.old.probability || 0}% to ${update.data.new.probability || 0}%`;
                                                                    } else if (update.data?.old && update.data?.new) {
                                                                        details = Object.entries(update.data.new)
                                                                            .filter(([key]) => !["updated_at", "created_at", "client_id", "project_id", "quotation_id"].includes(key))
                                                                            .map(([key, value]) => (
                                                                                <div key={key}>
                                                                                    <strong>{formatFieldName(key)}</strong> changed from <strong>{typeof update.data.old[key] === 'object' && update.data.old[key] !== null ? (update.data.old[key].name || update.data.old[key].id || '[Object]') : String(update.data.old[key] || '-')}</strong> to <strong>{typeof value === 'object' && value !== null ? (value.name || value.id || '[Object]') : String(value || '-')}</strong>
                                                                                </div>
                                                                            ));
                                                                    }
                                                                }

                                                                return (
                                                                    <div key={index} className="timeline-item">
                                                                        <div className="timeline-time">
                                                                            {moment(update.created_at || new Date()).format("h:mm A")}
                                                                        </div>
                                                                        <div className="timeline-icon">{getIcon(update.action)}</div>
                                                                        <div className="timeline-line"></div>
                                                                        <div className="timeline-content">
                                                                            <p className="timeline-title fw-semibold mb-1">{title}</p>
                                                                            {details && <div className="timeline-details small text-muted">{details}</div>}
                                                                            <p className="timeline-byline mt-1">
                                                                                <small className="text-muted">by {update.created_by || 'System'} • {moment(update.created_at || new Date()).format("MMM DD, YYYY")}</small>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="bg-light rounded p-5">
                                            <FaRegClock className="text-muted mb-3" size={32} />
                                            <h6 className="text-muted">No Timeline Activity</h6>
                                            <p className="text-muted small mb-0">
                                                There's no activity recorded in the timeline yet.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'permissions' && (
                            <div className="tab-pane active">
                                <DealPermissions
                                    dealId={dealId || id}
                                    dealData={dealDetails}
                                    fetchDealDetails={fetchDealDetails}
                                />
                            </div>
                        )}
                    </div>
                </Offcanvas.Body>

            </Offcanvas>

            {/* Deal Modal */}
            {showModal && (
                <DealModal
                    showModal={showModal}
                    handleClose={() => {
                        setShowModal(false);
                        setIsMinimizing(false);
                        setLocalShow(true);
                    }}
                    dealData={selectedDeal}
                    editDeal={editDeal}
                    fetchOrganisation={fetchOrganisation}
                    fetchContacts={fetchContacts}
                    contacts={contacts}
                    organisations={organisations}
                    dealId={dealId || id}
                />
            )}

            {/* Quotation Form Modal */}
            {showQuotationModal && (
                <QuotationForm
                    showModal={showQuotationModal}
                    handleClose={() => {
                        setShowQuotationModal(false);
                        setSelectedQuotationId(null);
                        setSelectedQuotationData(null);
                        setIsMinimizing(false);
                        setLocalShow(true);
                    }}
                    quotationId={selectedQuotationId}
                    quotationData={selectedQuotationData}
                    projectId={dealDetails?.project_id}
                    defaultClientId={dealDetails?.client_id}
                    projectName={dealDetails?.name || "Project Name"}
                    clientName={dealDetails?.organisation?.name || "Client Name"}
                    clientCurrency={dealDetails?.organisation?.currency || "AED"}
                    isOrder={isOrder}
                    products={products}
                    addQuotation={addQuotation}
                    setProducts={setProducts}
                    dealData={dealDetails}
                    editQuotation={editQuotation}
                />
            )}

            {/* Quotation Preview Modal */}
            {showQuotationPreviewModal && selectedQuotationForPreview && (
                <QuotationPreviewModal
                    showModal={showQuotationPreviewModal}
                    handleClose={() => {
                        setShowQuotationPreviewModal(false);
                        setSelectedQuotationForPreview(null);
                        setIsMinimizing(false);
                        setLocalShow(true);
                    }}
                    onEdit={handleEditFromPreview}
                    onDownload={() => handleDownloadPDF(
                        selectedQuotationForPreview.id,
                        selectedQuotationForPreview.ref_no
                    )}
                    clientName={selectedQuotationForPreview.project?.client?.name || "Client Name"}
                    projectName={selectedQuotationForPreview.project?.name || "Project Name"}
                    clientCurrency={selectedQuotationForPreview.project?.client?.currency || "AED"}
                    brands={[]}
                    categories={{ allSubcategories: [], allCategoriesList: [] }}
                    selectedProducts={selectedQuotationForPreview?.product_item_details || []}
                    customIntegrationDescription={selectedQuotationForPreview.custom_integration_description || ""}
                    customIntegrationTotal={selectedQuotationForPreview.custom_integration_total || 0}
                    formData={selectedQuotationForPreview}
                    editableDeploymentDescriptions={selectedQuotationForPreview.editable_deployment_descriptions || {}}
                    isUploaded={selectedQuotationForPreview.is_uploaded === true || selectedQuotationForPreview.is_uploaded === 1 || selectedQuotationForPreview.is_uploaded === '1'}
                    uploadedFileUrl={selectedQuotationForPreview.quotation_file}
                />
            )}

            {showOrganisationDetails && (
                <OrganisationDetails
                    showOffcanvas={showOrganisationDetails}
                    handleClose={handleCloseOrganisationDetails}
                    organisationId={selectedOrganisationIdForDetails}
                    onOrganisationUpdate={fetchDealDetails}
                />
            )}

            {/* Uploaded Quotation Edit Offcanvas */}
            {showUploadedQuotationEdit && (
                <QuotationUploadOffcanvas
                    show={showUploadedQuotationEdit}
                    handleClose={() => {
                        setShowUploadedQuotationEdit(false);
                        setSelectedUploadedQuotation(null);
                        setIsMinimizing(false);
                        setLocalShow(true);
                    }}
                    deal={dealDetails}
                    quotationData={selectedUploadedQuotation}
                    onSuccess={async (newQuot) => {
                        setShowUploadedQuotationEdit(false);
                        setSelectedUploadedQuotation(null);
                        setIsMinimizing(false);
                        setLocalShow(true);
                        await fetchDealDetails();
                        if (newQuot?.id) {
                            await fetchQuotationsForDeal(newQuot.id);
                        }
                    }}
                />
            )}
        </>
    );
};

export default DealDetails;
